import React from 'react';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import Calendar from '../Scheduler/Calendar';
import ColoredAlert from '../global/ColoredAlert';
import useCalendarFormFields from '../../hooks/appointments/useCalendarFormFields';
import { updateOfferedSlots } from '../../services/appointments';
import { Appointment } from '../../interfaces';
import {
  MAX_OFFERED_SLOTS,
  splitOfferedSlots,
  toggleOfferedSlot,
} from '../../utils/form/offeredSlots';

type Props = {
  open: boolean;
  appointment: Appointment;
  onClose: () => void;
  /** Receives the saved starts so the caller can refresh what it renders */
  onSaved: (starts: string[]) => void;
};

type PickerProps = {
  appointment: Appointment;
  offeredSlots: string[];
  elapsedCount: number;
  isSaving: boolean;
  onChange: (starts: string[]) => void;
  onSave: () => void;
  onClose: () => void;
};

// Inside the Formik provider, so it can read the artist and service like every
// other calendar input does
function Picker({
  appointment,
  offeredSlots,
  elapsedCount,
  isSaving,
  onChange,
  onSave,
  onClose,
}: PickerProps) {
  const { employeeId, serviceCategory, timezone } = useCalendarFormFields();

  return (
    <Calendar
      notice={
        elapsedCount ? (
          <ColoredAlert
            type="warning"
            subtitle={
              elapsedCount === 1
                ? 'A time you offered has already passed and was removed.'
                : `${elapsedCount} times you offered have already passed and were removed.`
            }
            dismissible={false}
            animate={false}
            hideIcon
          />
        ) : null
      }
      name="offeredSlots"
      employeeId={employeeId}
      appointmentLengthInMinutes={String(
        appointment.appointmentLengthInMinutes ?? '',
      )}
      startDateTime=""
      selectedDateTimes={offeredSlots}
      maxSelections={MAX_OFFERED_SLOTS}
      onChange={value =>
        onChange(toggleOfferedSlot(offeredSlots, value.toISOString()))
      }
      disabled={isSaving}
      serviceCategory={serviceCategory}
      timezone={timezone}
      // The picker is the whole dialog: it opens straight away and saves
      openOnMount
      hideTrigger
      doneLabel="Send times"
      onDone={onSave}
      onDismiss={onClose}
    />
  );
}

export default function EditOfferedTimesDialog({
  open,
  appointment,
  onClose,
  onSaved,
}: Props) {
  const [isSaving, setIsSaving] = React.useState(false);
  // Split once on the way in: the notice is about what was there when the
  // dialog opened, not about what the artist has picked since
  const [initial] = React.useState(() =>
    splitOfferedSlots(appointment.offeredSlots),
  );
  const [offeredSlots, setOfferedSlots] = React.useState<string[]>(
    initial.live,
  );

  const onSave = async () => {
    setIsSaving(true);
    try {
      await updateOfferedSlots(appointment, offeredSlots);
      toast.success('New times sent');
      onSaved(offeredSlots);
      onClose();
    } catch {
      toast.error("Couldn't send the new times. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Formik
      initialValues={{
        employeeId: appointment.employeeId ?? '',
        serviceId: appointment.serviceId ? String(appointment.serviceId) : '0',
        businessTripId: appointment.businessTripId ?? '',
      }}
      onSubmit={() => {}}>
      <Picker
        appointment={appointment}
        offeredSlots={offeredSlots}
        elapsedCount={initial.elapsed}
        isSaving={isSaving}
        onChange={setOfferedSlots}
        onSave={onSave}
        onClose={onClose}
      />
    </Formik>
  );
}
